import 'knockout.validation';
import * as ko from 'knockout';

import { PartnerStatusEnum } from '../Common/enums';
import {
    IAddKidRequest,
    IAddPartnerRequest,
    IKid,
    IAdultHouseholdMember,
    IAdultHouseholdMemberDto,
    IPatientDto,
    ISelfResponse,
    IStatus,
    IUpdateSelfRequest,
    IDeleteAdultHouseholdMemberRequest,
} from '../Common/interfaces';
import { Log } from '../Common/Log';
import { Texts } from '../Common/Texts';
import { Utils } from '../Common/Utils';
import { AlertViewModel } from './AlertViewModel';
import { ReceiveVideoCall } from './consultations/ReceiveVideoCallFromDoctor';
import Modal from '../Common/Modal';

export class ProfileViewModel {
    public receiveVideoCall: ReceiveVideoCall;

    public profileState: KnockoutObservable<string>;
    public isSubmitting: KnockoutComputed<boolean>;
    public profileErrorMessage: KnockoutObservable<string>;

    public isSubscriptionOwner: KnockoutObservable<boolean>;

    public name: KnockoutObservable<string>;
    public modalName: KnockoutObservable<string>;
    public modalCPR: KnockoutObservable<string>;
    public modalEmail: KnockoutObservable<string>;
    public CPR: KnockoutObservable<string>;
    public cprFormatted: KnockoutObservable<string>;
    public email: KnockoutObservable<string>;
    public phone: KnockoutObservable<string>;
    public emailEdit: KnockoutObservable<string>;
    public phoneEdit: KnockoutObservable<string>;

    public policyNumber: KnockoutObservable<string>;
    public hasPolicyNumber: KnockoutComputed<boolean>;
    public kids: KnockoutObservableArray<IKid>;
    public adultHouseholdMembers: KnockoutObservableArray<IAdultHouseholdMember>;

    public partnerEmail: KnockoutObservable<string>;
    public partnerName: KnockoutObservable<string>;
    public partnerCpr: KnockoutObservable<string>;
    public partnerStatus: KnockoutObservable<PartnerStatusEnum>;
    public hasPartner: KnockoutComputed<boolean>;

    public error: KnockoutValidationGroup;
    public childModalError: KnockoutValidationGroup;
    public partnerModalError: KnockoutValidationGroup;
    public profileError: KnockoutValidationGroup;
    public selectedKidUid: KnockoutObservable<string>;
    public selectedPartnerToDelete: IAdultHouseholdMember;
    public selectedKidToDelete: IKid;

    public newsletterSwitch = ko.observable(false);
    public newsletterSwitchDisabled = ko.observable(false);

    private profileStates = {
        ready: 'ready',
        saving: 'saving',
        saved: 'saved',
    };

    private currentPatient: IPatientDto;
    private alertModal: AlertViewModel;

    constructor() {
        const self = this;
        self.alertModal = new AlertViewModel();
        self.receiveVideoCall = new ReceiveVideoCall();

        self.profileState = ko.observable(self.profileStates.ready);
        self.isSubmitting = ko.computed(() => {
            return self.profileState() == self.profileStates.saving;
        });
        self.profileErrorMessage = ko.observable(null);

        self.isSubscriptionOwner = ko.observable(false);
        self.name = ko.observable('').extend({
            required: {
                param: true,
                message: Texts.getResource('NameRequired'),
            },
            fullname: true,
        });
        self.CPR = ko
            .observable('')
            .extend({ cprClean: true })
            .extend({
                required: {
                    param: true,
                    message: Texts.getResource('CprRequired'),
                },
                pattern: {
                    message: Texts.getResource('InvalidBirthdate'),
                    params: '^[0-9]{10}$',
                },
            });
        self.cprFormatted = ko.pureComputed(function () {
            return Utils.formatCpr(self.CPR());
        });
        self.email = ko.observable('').extend({
            required: {
                param: true,
                message: Texts.getResource('EmailRequired'),
            },
            maxLength: 256,
            email: {
                message: Texts.getResource('InvalidEmail'),
            },
        });
        self.emailEdit = ko.observable('').extend({
            required: {
                param: true,
                message: Texts.getResource('EmailRequired'),
            },
            maxLength: 256,
            email: {
                message: Texts.getResource('InvalidEmail'),
            },
        });
        self.phone = ko.observable(null).extend({
            pattern: {
                params: '^[+]?[0-9]{8,20}$',
                message: Texts.getResource('InvalidPhoneNumber'),
            },
            validation: {
                validator: function (val: string) {
                    return val !== null && val.trim() !== '';
                },
                message: Texts.getResource('PhoneNumberRequired'),
            },
        });
        self.phoneEdit = ko.observable(null).extend({
            pattern: {
                params: '^[+]?[0-9]{8,20}$',
                message: Texts.getResource('InvalidPhoneNumber'),
            },
            validation: {
                validator: function (val: string) {
                    return val !== null && val.trim() !== '';
                },
                message: Texts.getResource('PhoneNumberRequired'),
            },
        });
        self.policyNumber = ko.observable('');

        self.hasPolicyNumber = ko.pureComputed(() => {
            return self.policyNumber() && self.policyNumber().length > 0;
        });

        self.modalName = ko.observable('').extend({
            required: {
                param: true,
                message: Texts.getResource('NameRequired'),
            },
            fullname: {
                param: true,
                message: 'Indtast venligst det fulde navn',
            },
        });
        self.modalCPR = ko
            .observable('')
            .extend({ cprClean: true })
            .extend({
                required: {
                    param: true,
                    message: Texts.getResource('CprRequired'),
                },
                cpr: true,
                underCertainAge: 18,
            });
        self.modalEmail = ko.observable('').extend({
            required: {
                param: true,
                message: Texts.getResource('EmailRequired'),
            },
            maxLength: 75,
            email: {
                message: Texts.getResource('InvalidEmail'),
            },
        });

        self.profileError = ko.validatedObservable([
            self.phoneEdit,
            self.emailEdit,
        ]);
        self.profileError.errors.showAllMessages(false);

        self.kids = ko.observableArray([]);
        self.childModalError = ko.validatedObservable([
            self.modalCPR,
            self.modalName,
        ]);
        self.childModalError.errors.showAllMessages(false);

        self.adultHouseholdMembers = ko.observableArray([]);
        self.hasPartner = ko.pureComputed(() => {
            return (
                self.adultHouseholdMembers() &&
                self.adultHouseholdMembers().length > 0
            );
        });

        self.partnerEmail = ko.observable(null).extend({
            required: {
                param: true,
                message: Texts.getResource('PartnerEmailRequired'),
            },
            maxLength: 75,
            email: {
                message: Texts.getResource('InvalidEmail'),
            },
        });
        self.partnerName = ko.observable(null).extend({
            required: {
                param: true,
                message: Texts.getResource('PartnerNameRequired'),
            },
        });
        self.partnerCpr = ko
            .observable(null)
            .extend({ cprClean: true })
            .extend({
                required: {
                    param: true,
                    message: Texts.getResource('PartnerCprRequired'),
                },
                cpr: true,
                overAndIncludingCertainAge: 18,
            });
        self.partnerStatus = ko.observable(null);

        self.partnerModalError = ko.validatedObservable([
            self.partnerEmail,
            self.partnerName,
            self.partnerCpr,
        ]);
        self.partnerModalError.errors.showAllMessages(false);

        self.selectedKidUid = ko.observable(null);
        self.selectedPartnerToDelete = null;
        self.error = ko.validatedObservable([
            self.name,
            self.CPR,
            self.email,
            self.phone,
        ]);
        self.error.errors.showAllMessages(false);

        self.getProfileData();
    }

    public getProfileData = (onlyPartnerData = false) => {
        const self = this;

        const callBackAjax = (
            response: ISelfResponse,
            status: string,
            jqXhr: JQueryXHR,
        ): void => {
            this.getProfileDataCallback(response, onlyPartnerData);
        };

        const errorCallBack = (
            jqXhr: JQueryXHR,
            textStatus: string,
            error: string,
        ): void => {
            self.alertModal.openAlert(
                'Der opstod en fejl! Prøv venligst at genindlæse siden.',
            );
        };

        Utils.ajaxCall(
            'GET',
            'api/patients/self',
            callBackAjax,
            errorCallBack,
            null,
            null,
            false,
        );
    };

    public getProfileDataCallback(
        response: ISelfResponse,
        onlyPartnerData: boolean,
    ) {
        const self = this;
        if (!onlyPartnerData) {
            if (!response.Me || !response.Me.User) {
                Log.error('getProfileDataCallback: User not set', response);
                self.alertModal.openAlert(
                    'Der opstod en fejl! Prøv venligst at genindlæse siden.',
                );
                return;
            }
            self.name(response.Me.User.FullName);
            self.CPR(response.Me.CPR);
            self.email(response.Me.Email);
            self.phone(response.Me.MobilePhone);
            self.policyNumber(response.Me.PolicyNumber);
            self.isSubscriptionOwner(response.Me.IsSubscriptionOwner);
            self.newsletterSwitch(response.Me.AcceptMarketing);
            self.newsletterSwitch.subscribe((newValue) =>
                this.toggleNewsletterSwitch(newValue),
            );

            self.kids.removeAll();
            response.Kids.forEach((item: IPatientDto) => {
                const currentKid: IKid = {
                    Name: item.User.FullName,
                    UID: item.User.UID,
                    CPR: item.CPR,
                    FormattedCpr: Utils.formatCpr(item.CPR),
                };
                self.kids.push(currentKid);
            });

            self.currentPatient = response.Me;

            self.profileError.errors.showAllMessages(false);
        }

        if (response.AdultHouseholdMembers !== null) {
            self.adultHouseholdMembers([]);
            response.AdultHouseholdMembers.forEach(
                (item: IAdultHouseholdMemberDto) => {
                    const currentAdultHouseholdMember: IAdultHouseholdMember = {
                        ID: item.ID,
                        UID: item.UID,
                        Name: item.Name,
                        Email: item.Email,
                        Status: item.Status,
                        Cpr: item.Cpr,
                    };
                    self.adultHouseholdMembers.push(
                        currentAdultHouseholdMember,
                    );
                },
            );
        }
    }

    public saveProfileData(): void {
        const self = this;

        if (!self.profileError.isValid()) {
            self.profileState(self.profileStates.ready);
            self.profileError.errors.showAllMessages();
            self.profileErrorMessage(Texts.getResource('FormInvalidValue'));

            return;
        }

        self.profileState(self.profileStates.saving);
        self.profileErrorMessage('');

        const request: IUpdateSelfRequest = {
            Name: self.name(),
            Email: self.emailEdit(),
            CPR: self.CPR(),
            Mobile: self.phoneEdit(),
        };

        const callBackAjax = (
            data: IStatus<ISelfResponse>,
            status: string,
            jqXhr: JQueryXHR,
        ): void => {
            if (data.HasErrors) {
                const error = data.Errors.map((error) => error.Value).join(
                    '\n',
                );
                self.profileErrorMessage(error);
                self.profileState(self.profileStates.ready);
                return;
            }

            this.email(this.emailEdit());
            this.phone(this.phoneEdit());

            self.profileState(self.profileStates.saved);
            self.getProfileDataCallback(data.Result, false);
            Modal.close('editMyInfoModal');
        };

        const errorCallBack = (
            jqXhr: JQueryXHR,
            textStatus: string,
            error: string,
        ): void => {
            const json = JSON.parse(jqXhr.responseText);
            self.profileErrorMessage(json.Message || json.Title);
            self.profileState(self.profileStates.ready);
            Modal.close('editMyInfoModal');
        };

        Utils.ajaxCall(
            'POST',
            'api/patients/self/v2',
            callBackAjax,
            errorCallBack,
            JSON.stringify(request),
        );
    }

    public addChild = () => {
        const self = this;

        if (self.profileState() !== self.profileStates.ready) {
            return;
        }

        self.profileState(self.profileStates.saving);

        if (!self.childModalError.isValid()) {
            self.profileState(self.profileStates.ready);
            self.childModalError.errors.showAllMessages(true);
            return;
        }

        const request: IAddKidRequest = {
            Name: self.modalName(),
            CPR: self.modalCPR(),
        };

        const callBackAjax = (
            response: IStatus<string>,
            textStatus: string,
            jqXhr: JQueryXHR,
        ): void => {
            if (response.HasErrors) {
                this.alertModal.openAlert(
                    response.Errors.map((e) => e.Value).join('\n'),
                );
                self.profileState(self.profileStates.ready);
                Modal.close('addChildModal');
                return;
            }
            const newKid: IKid = {
                Name: self.modalName(),
                CPR: self.modalCPR(),
                UID: response.Result,
            };
            self.kids.push(newKid);
            Modal.close('addChildModal');
            self.profileState(self.profileStates.ready);
            window.location.reload();
        };

        const errorCallBack = (
            jqXhr: JQueryXHR,
            textStatus: string,
            error: string,
        ): void => {
            self.alertModal.openAlert('Kunne ikke oprette barn: ' + error);
            self.profileState(self.profileStates.ready);
            Modal.close('addChildModal');
        };

        Utils.ajaxCall(
            'POST',
            'api/patients/child',
            callBackAjax,
            errorCallBack,
            JSON.stringify(request),
        );
    };

    public invitePartner = () => {
        const self = this;

        if (!self.partnerModalError.isValid()) {
            self.partnerModalError.errors.showAllMessages(true);
            return;
        }

        const request: IAddPartnerRequest = {
            Name: self.partnerName(),
            Email: self.partnerEmail(),
            Cpr: self.partnerCpr(),
        };

        const callBackAjax = (
            data: IStatus<any>,
            status: string,
            jqXhr: JQueryXHR,
        ): void => {
            if (data.HasErrors) {
                const error = data.Errors.map((error) => error.Value).join(
                    '\n',
                );
                this.alertModal.openAlert(error);
                self.profileState(self.profileStates.ready);
                Modal.close('invitePartnerModal');
                return;
            }
            self.getProfileData(true);
            Modal.close('invitePartnerModal');
        };

        const errorCallBack = (
            jqXhr: JQueryXHR,
            textStatus: string,
            error: string,
        ): void => {
            this.alertModal.openAlert(Texts.getResource('ErrorInvitePartner'));
        };

        Utils.ajaxCall(
            'POST',
            'api/patients/adult-household-member',
            callBackAjax,
            errorCallBack,
            JSON.stringify(request),
        );
    };

    public addChildModal = () => {
        const self = this;
        self.selectedKidUid(null);
        self.modalName('');
        self.modalCPR(null);
        self.childModalError.errors.showAllMessages(false);
        self.profileState(self.profileStates.ready);
        self.setModalTitle();
        Modal.show('addChildModal');
    };

    public invitePartnerModal = () => {
        const self = this;
        self.partnerEmail(null);
        self.partnerName(null);
        self.partnerCpr(null);
        self.modalName('');
        self.modalEmail('');
        self.partnerModalError.errors.showAllMessages(false);
        self.profileState(self.profileStates.ready);
        Modal.show('invitePartnerModal');
    };

    public editChild = (kid: IKid) => {
        const self = this;
        self.modalName(kid.Name);
        self.modalCPR(kid.CPR);
        self.selectedKidUid(kid.UID);
        self.childModalError.errors.showAllMessages(false);
        self.profileState(self.profileStates.ready);
        self.setModalTitle();
        Modal.show('addChildModal');
    };

    public editMyInfo = () => {
        this.profileErrorMessage('');
        this.phoneEdit(this.phone());
        this.emailEdit(this.email());
        const self = this;
        self.profileState(self.profileStates.ready);
        Modal.show('editMyInfoModal');
    };

    public updateKids(): void {
        const self = this;

        if (self.profileState() !== self.profileStates.ready) {
            return;
        }

        self.profileState(self.profileStates.saving);

        if (!self.childModalError.isValid()) {
            self.childModalError.errors.showAllMessages(true);
            return;
        }

        const kidToUpdate: IKid = {
            CPR: self.modalCPR().toString(),
            UID: self.selectedKidUid(),
            Name: self.modalName(),
        };

        const callBackAjax = (response: any, status: any, error: any): void => {
            const kidOldValue = ko.utils.arrayFirst(self.kids(), (item) => {
                return item.UID === self.selectedKidUid();
            });

            const kidNewValue: IKid = {
                CPR: self.modalCPR().toString(),
                Name: self.modalName(),
                UID: kidOldValue.UID,
                FormattedCpr: Utils.formatCpr(self.modalCPR().toString()),
            };

            self.kids.replace(kidOldValue, kidNewValue);
            Modal.close('addChildModal');
        };

        const errorCallBack = (
            jqXhr: JQueryXHR,
            textStatus: string,
            error: string,
        ): void => {
            self.profileState(self.profileStates.ready);
            self.alertModal.openAlert(
                'Error occured while updating child data',
            );
        };

        Utils.ajaxCall(
            'PUT',
            'api/patients/kid',
            callBackAjax,
            errorCallBack,
            JSON.stringify(kidToUpdate),
        );
    }

    public openDeletePartnerModal(
        partnerToDelete: IAdultHouseholdMember,
    ): void {
        this.selectedPartnerToDelete = partnerToDelete;
        Modal.show('confirm-modal');
    }

    public openDeleteKidModal(kidToDelete: IKid): void {
        this.selectedKidToDelete = kidToDelete;
        Modal.show('confirm-modal-kid');
    }

    public closeDeleteKidModal(): void {
        Modal.close('confirm-modal-kid');
    }

    public closeConfirmModal(): void {
        Modal.close('confirm-modal');
    }

    public deletePartner(): void {
        const self = this;
        const callBackAjax = (
            data: any,
            textStatus: string,
            jqXhr: JQueryXHR,
        ): void => {
            this.getProfileData(true);
        };

        const errorCallBack = (
            jqXhr: JQueryXHR,
            textStatus: string,
            error: string,
        ): void => {
            this.alertModal.openAlert(Texts.getResource('ErrorDeletePartner'));
        };

        const request: IDeleteAdultHouseholdMemberRequest = {
            Cpr: self.selectedPartnerToDelete.Cpr,
        };

        Utils.ajaxCall(
            'POST',
            'api/patients/adult-household-member/delete',
            callBackAjax,
            errorCallBack,
            JSON.stringify(request),
        );

        this.closeConfirmModal();
    }

    public deleteChild(): void {
        const self = this;
        const callBackAjax = (
            response: IStatus<any>,
            status: any,
            error: any,
        ): void => {
            this.closeDeleteKidModal();
            if (response.HasErrors) {
                this.alertModal.openAlert(
                    response.Errors.map((e) => e.Value).join('\n'),
                );
                return;
            } else {
                this.getProfileData(true);
                window.location.reload();
            }
        };

        const errorCallBack = (
            jqXhr: JQueryXHR,
            textStatus: string,
            error: string,
        ): void => {
            // TODO: this translation have never existed, but we don't support kids anyway
            //this.alertModal.openAlert(Texts.getResource('ErrorDeleteChild'));
        };

        Utils.ajaxCall(
            'DELETE',
            'api/patients/kid/' + self.selectedKidToDelete.UID,
            callBackAjax,
            errorCallBack,
            null,
            null,
            false,
        );
    }

    public deleteAccount(): void {
        const callBackAjax = (
            data: any,
            textStatus: string,
            jqXhr: JQueryXHR,
        ): void => {
            this.closeDeleteAccountModal();
            this.logout();
        };
        const errorCallBack = (
            jqXhr: JQueryXHR,
            textStatus: string,
            error: string,
        ): void => {
            this.closeDeleteAccountModal();
            this.alertModal.openAlert(Texts.getResource('ErrorDeleteAccount'));
        };

        Utils.ajaxCall(
            'DELETE',
            'api/patients/account',
            callBackAjax,
            errorCallBack,
            null,
            null,
            false,
        );
    }

    public openDeleteAccountModal(): void {
        Modal.show('delete-account-modal');
    }

    public closeDeleteAccountModal(): void {
        Modal.close('delete-account-modal');
    }

    public openRemoveInsurancePartnerModal(): void {
        Modal.show('remove-insurance-partner-modal');
    }

    public closeRemoveInsurancePartnerModal(): void {
        Modal.close('remove-insurance-partner-modal');
    }

    public openUnsubscribeModal(): void {
        Modal.show('confirm-unsubscribe-modal');
    }

    public closeUnsubscribeModal(): void {
        Modal.close('confirm-unsubscribe-modal');
    }

    public logout(): boolean {
        Utils.logout();
        return true;
    }

    public setModalTitle() {
        const titleElement = document.getElementById('addChildModal-title');
        if (titleElement) {
            if (this.selectedKidUid() == null) {
                titleElement.textContent =
                    Texts.getResource('Profile_AddChild');
            } else {
                titleElement.textContent =
                    Texts.getResource('Profile_EditChild');
            }
        }
    }

    public toggleNewsletterSwitch(newValue: boolean) {
        const self = this;
        self.newsletterSwitchDisabled(true);

        const callBack = (
            response: ISelfResponse,
            status: string,
            jqXhr: JQueryXHR,
        ): void => {
            self.newsletterSwitchDisabled(false);
        };

        const errorCallBack = (
            jqXhr: JQueryXHR,
            textStatus: string,
            error: string,
        ): void => {
            self.newsletterSwitchDisabled(false);
        };

        if (newValue) {
            Utils.ajaxCall(
                'GET',
                `email/subscribe?email=${encodeURIComponent(
                    self.email(),
                )}&getView=false`,
                callBack,
                errorCallBack,
            );
        } else {
            Utils.ajaxCall(
                'GET',
                `email/unsubscribe?email=${encodeURIComponent(
                    self.email(),
                )}&getView=false`,
                callBack,
                errorCallBack,
            );
        }
    }
}
